import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, X, Square } from "lucide-react";
import { api, gbp, formatApiErrorDetail } from "../../lib/api";

const inputCls = "w-full bg-transparent border border-[#27272A] px-3 py-2.5 text-sm outline-none focus:border-[#FFCC00] placeholder:text-white/30";
const EMPTY = { title: "", condition: "Grade A/B Mixed — Manifested", units: "", image: "", description: "", start_price: "", floor_price: "", drop_amount: "", interval_seconds: "60", start_in_hours: "0" };

const STATE_STYLE = {
  live: "border-[#FF3B30] text-[#FF3B30]",
  scheduled: "border-[#FFCC00] text-[#FFCC00]",
  claimed: "border-green-500 text-green-500",
  ended: "border-white/30 text-white/40",
};

export const AdminAuctions = () => {
  const [auctions, setAuctions] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/admin/auctions").then(({ data }) => setAuctions(data));
  useEffect(() => { load(); const t = setInterval(load, 15000); return () => clearInterval(t); }, []);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/admin/auctions", {
        title: form.title, condition: form.condition, units: parseInt(form.units),
        image: form.image, description: form.description,
        start_price: parseFloat(form.start_price), floor_price: parseFloat(form.floor_price),
        drop_amount: parseFloat(form.drop_amount), interval_seconds: parseInt(form.interval_seconds),
        start_in_hours: parseFloat(form.start_in_hours) || 0,
      });
      toast.success(parseFloat(form.start_in_hours) > 0 ? "Auction scheduled" : "Auction is LIVE — price is falling");
      setShowForm(false);
      setForm(EMPTY);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const endAuction = async (id) => {
    await api.patch(`/admin/auctions/${id}`, { status: "ended" });
    toast.success("Auction pulled");
    load();
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h2 className="font-display text-2xl">ALL AUCTIONS ({auctions.length})</h2>
        <button data-testid="admin-new-auction-button" onClick={() => setShowForm(!showForm)} className="btn-industrial px-5 py-2.5 text-lg flex items-center gap-2">
          <Plus size={16} /> NEW AUCTION
        </button>
      </div>

      {showForm && (
        <form onSubmit={save} className="border-2 border-[#FF3B30] p-6 sm:p-8 mb-10 bg-[#111111]" data-testid="admin-auction-form">
          <div className="flex justify-between items-center mb-6">
            <h3 className="font-display text-2xl">CREATE REVERSE AUCTION</h3>
            <button type="button" onClick={() => setShowForm(false)} className="text-white/40 hover:text-[#FF3B30]"><X size={20} /></button>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <input data-testid="auction-form-title" required placeholder="TITLE" value={form.title} onChange={set("title")} className={`${inputCls} sm:col-span-2`} />
            <input data-testid="auction-form-condition" required placeholder="CONDITION GRADE" value={form.condition} onChange={set("condition")} className={inputCls} />
            <input data-testid="auction-form-units" required type="number" min="1" placeholder="TOTAL UNITS" value={form.units} onChange={set("units")} className={inputCls} />
            <input data-testid="auction-form-start-price" required type="number" step="0.01" min="1" placeholder="START PRICE £" value={form.start_price} onChange={set("start_price")} className={inputCls} />
            <input data-testid="auction-form-floor-price" required type="number" step="0.01" min="0" placeholder="FLOOR PRICE £" value={form.floor_price} onChange={set("floor_price")} className={inputCls} />
            <input data-testid="auction-form-drop" required type="number" step="0.01" min="0.01" placeholder="DROP AMOUNT £ PER INTERVAL" value={form.drop_amount} onChange={set("drop_amount")} className={inputCls} />
            <input data-testid="auction-form-interval" required type="number" min="5" placeholder="DROP INTERVAL SECONDS" value={form.interval_seconds} onChange={set("interval_seconds")} className={inputCls} />
            <input data-testid="auction-form-start-in" type="number" step="0.5" min="0" placeholder="START IN HOURS (0 = NOW)" value={form.start_in_hours} onChange={set("start_in_hours")} className={inputCls} />
            <input data-testid="auction-form-image" required placeholder="IMAGE URL" value={form.image} onChange={set("image")} className={inputCls} />
            <textarea data-testid="auction-form-description" required rows={3} placeholder="DESCRIPTION" value={form.description} onChange={set("description")} className={`${inputCls} sm:col-span-2`} />
          </div>
          <button data-testid="auction-form-save-button" disabled={busy} className="btn-industrial px-8 py-3 text-xl mt-6">
            {busy ? "CREATING…" : "LAUNCH AUCTION"}
          </button>
        </form>
      )}

      <div className="border border-[#27272A] overflow-x-auto">
        <table className="w-full text-xs sm:text-sm min-w-[760px]" data-testid="admin-auctions-table">
          <thead>
            <tr className="text-left text-white/40 uppercase text-[10px] tracking-widest border-b border-[#27272A]">
              <th className="px-4 py-3">SKU</th><th className="px-4 py-3">Title</th><th className="px-4 py-3">State</th>
              <th className="px-4 py-3 text-right">Price now</th><th className="px-4 py-3 text-right">Start → Floor</th>
              <th className="px-4 py-3">Claimed by</th><th className="px-4 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {auctions.map((a) => (
              <tr key={a.id} className="border-b border-[#27272A] last:border-0 hover:bg-[#111111]">
                <td className="px-4 py-3 text-white/50">{a.sku}</td>
                <td className="px-4 py-3">{a.title}</td>
                <td className="px-4 py-3">
                  <span className={`text-[10px] uppercase tracking-widest px-2 py-1 border ${STATE_STYLE[a.state]}`}>{a.state}</span>
                </td>
                <td className="px-4 py-3 text-right text-[#FFCC00]">{a.current_price != null ? gbp(a.current_price) : "—"}</td>
                <td className="px-4 py-3 text-right text-white/50">{gbp(a.start_price)} → {gbp(a.floor_price)}</td>
                <td className="px-4 py-3 text-white/60">{a.claimed_by_name || "—"}</td>
                <td className="px-4 py-3 text-right">
                  {(a.state === "live" || a.state === "scheduled") && (
                    <button data-testid={`admin-end-auction-${a.slug}`} onClick={() => endAuction(a.id)} title="End auction" className="text-white/40 hover:text-[#FF3B30] p-1"><Square size={14} /></button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};
